<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import WidgetModalShell from '@/components/Dashboard/Modals/WidgetModalShell.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { statusPanelWidgetSchema } from '@/schemas/WidgetModalSchema'
import { SensorType } from '@/interfaces/enums'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import { useI18n } from '@/composable/useI18n'

const props = withDefaults(
  defineProps<{
    sensors: ISensor[]
    initialData?: IWidget
    loading?: boolean
  }>(),
  { initialData: undefined, loading: false },
)

const emit = defineEmits<{
  save: [formData: IWidgetFormData]
  cancel: []
}>()

const { t } = useI18n()
const selectedSensor = ref<ISensor | null>(null)

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const doorOpenSensors = computed(() =>
  props.sensors.filter((s) => s.sensor_type === SensorType.DOOR_OPEN),
)

const modalTitle = computed(() =>
  props.initialData
    ? t('custom_dashboards.widget_modal.status_panel.title_edit')
    : t('custom_dashboards.widget_modal.status_panel.title_create'),
)

const initialValues = computed(() => {
  if (props.initialData) {
    const sensorId = props.initialData.config.sensor_ids?.[0] || ''
    return {
      title: props.initialData.title,
      sensorId,
    }
  }

  return {
    title: t('custom_dashboards.widget_modal.status_panel.default_title'),
    sensorId: '',
  }
})

watch(
  () => [props.initialData, doorOpenSensors.value] as const,
  () => {
    if (!props.initialData) {
      selectedSensor.value = null
      return
    }

    const sensorId = props.initialData.config.sensor_ids?.[0] || ''
    selectedSensor.value = doorOpenSensors.value.find((s) => String(s.id) === sensorId) || null
  },
  { immediate: true },
)

function onSensorSelected(sensor: ISensor, setFieldValue: (field: string, value: unknown) => void) {
  selectedSensor.value = sensor
  setFieldValue('sensorId', String(sensor.id))
}

function onSubmit(values: { title: string; sensorId: string }) {
  emit('save', {
    title: values.title,
    sensorId: values.sensorId,
  })
}
</script>

<template>
  <WidgetModalShell :loading="loading" @cancel="emit('cancel')">
    <template #title>
      <h2 class="font-satoshi mb-6 text-xl font-bold text-black">
        {{ modalTitle }}
      </h2>
    </template>

    <Form
          :validation-schema="statusPanelWidgetSchema"
          :initial-values="initialValues"
          @submit="onSubmit"
          v-slot="{ setFieldValue, errors }"
        >
          <div class="space-y-5">
            <Field name="title" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.status_panel.fields.title') }}
                </label>
                <input
                  v-bind="field"
                  type="text"
                  :class="fieldInputClass"
                  :placeholder="t('custom_dashboards.widget_modal.status_panel.placeholders.title')"
                />
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <div>
              <label :class="fieldLabelClass">
                {{ t('custom_dashboards.widget_modal.status_panel.fields.sensor') }}
              </label>
              <DropdownSensors
                name="sensorId"
                :options="doorOpenSensors"
                :already-selected-option="selectedSensor || undefined"
                :constrain-to-parent="true"
                @option-selected="(sensor: ISensor) => onSensorSelected(sensor, setFieldValue)"
              />
              <p v-if="errors.sensorId" :class="fieldErrorClass">{{ errors.sensorId }}</p>
            </div>

            <div class="border-l-4 border-blue bg-blue-50 px-4 py-3">
              <p class="text-sm leading-relaxed text-body">
                {{ t('custom_dashboards.widget_modal.status_panel.info') }}
              </p>
            </div>
          </div>

          <div class="mt-6 border-t border-stroke pt-6">
            <div class="flex justify-end gap-4">
              <button
                type="button"
                class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="loading"
                @click="emit('cancel')"
              >
                {{ t('custom_dashboards.widget_modal.status_panel.actions.cancel') }}
              </button>
              <button
                type="submit"
                class="flex h-10 min-w-[7rem] items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="loading"
              >
                <ButtonLoader v-if="loading" />
                <span v-else>
                  {{
                    initialData
                      ? t('custom_dashboards.widget_modal.status_panel.actions.save')
                      : t('custom_dashboards.widget_modal.status_panel.actions.create')
                  }}
                </span>
              </button>
            </div>
          </div>
        </Form>
  </WidgetModalShell>
</template>